import type { NodeViewProps } from '@tiptap/react';
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react';
import { Check, Copy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LowlightInstance {
  listLanguages: () => string[];
}

const getLowlight = (extension: NodeViewProps['extension']): LowlightInstance | null => {
  const options = extension.options as { lowlight?: LowlightInstance };
  return options.lowlight ?? null;
};

export const CodeBlockNodeView = ({ node, updateAttributes, extension }: NodeViewProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // Track the copied-affordance timer so unmount can clear it - otherwise React 18
  // warns when the block is deleted within the 1500ms window.
  const copiedTimerRef = useRef<number | null>(null);

  const languages = useMemo(() => {
    const lowlight = getLowlight(extension);
    return lowlight ? lowlight.listLanguages().sort() : [];
  }, [extension]);

  const currentLanguage = (node.attrs.language as string | null) ?? 'auto';

  const onLanguageChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const next = event.target.value;
      updateAttributes({ language: next === 'auto' ? null : next });
    },
    [updateAttributes]
  );

  const onCopy = useCallback(async () => {
    const text = node.textContent;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return;
    }

    setCopied(true);
    if (copiedTimerRef.current !== null) {
      window.clearTimeout(copiedTimerRef.current);
    }

    copiedTimerRef.current = window.setTimeout(() => {
      copiedTimerRef.current = null;
      setCopied(false);
    }, 1500);
  }, [node]);

  useEffect(
    () => () => {
      if (copiedTimerRef.current !== null) {
        window.clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
    },
    []
  );

  return (
    <NodeViewWrapper className="tiptap-code-block">
      <div className="tiptap-code-block-toolbar" contentEditable={false}>
        <select
          className="tiptap-code-block-language"
          value={currentLanguage}
          onChange={onLanguageChange}
          aria-label={t('tiptap-code-block-language')}
        >
          <option value="auto">{t('tiptap-language-auto')}</option>
          {languages.map((lang) => (
            <option key={lang} value={lang}>
              {lang}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="tiptap-code-block-copy"
          onClick={onCopy}
          aria-label={copied ? t('copied') : t('tiptap-copy-code')}
          title={copied ? t('copied') : t('tiptap-copy-code')}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          <span>{copied ? t('copied') : t('copy')}</span>
        </button>
      </div>
      <pre>
        <NodeViewContent<'code'> as="code" />
      </pre>
    </NodeViewWrapper>
  );
};
